import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  TotpMultiFactorGenerator,
  TotpSecret,
  MultiFactorError,
  multiFactor,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiService } from './api';

export interface MfaStatus {
  requiresEnrollment: boolean;
  isEmailPassword: boolean;
  isGoogleOnly: boolean;
  mfaRequired: boolean;
  mfaEnabled: boolean;
  mfaEnrolled: boolean;
}

export const mfaService = {
  /**
   * Check if current user needs MFA enrollment
   */
  async checkStatus(): Promise<MfaStatus> {
    const response = await apiService.get<{ success: boolean; data: MfaStatus }>('/mfa/status');
    return response.data;
  },

  /**
   * Mark user as enrolled in MFA (called after successful enrollment)
   */
  async markEnrolled(): Promise<void> {
    await apiService.post('/mfa/enrolled');
  },

  /**
   * Verify MFA enrollment status
   */
  async verifyEnrollment(): Promise<{ enrolled: boolean }> {
    const response = await apiService.get<{ success: boolean; data: { enrolled: boolean } }>('/mfa/verify');
    return response.data;
  },

  /**
   * Enroll in SMS MFA
   * @param phoneNumber Phone number in E.164 format (e.g., +1234567890)
   * @param recaptchaVerifier reCAPTCHA verifier instance
   */
  async enrollSms(phoneNumber: string, recaptchaVerifier: any): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated. Please refresh the page and try again.');
    }

    // In Firebase v9+, use multiFactor() function instead of user.multiFactor property
    // No need to check - multiFactor() will work if user is authenticated

    try {
      const phoneProvider = new PhoneAuthProvider(auth);

      // Send verification code
      const verificationId = await phoneProvider.verifyPhoneNumber(
        phoneNumber,
        recaptchaVerifier
      );

      return verificationId;
    } catch (error: any) {
      console.error('Error sending SMS verification:', error);
      if (error.code === 'auth/invalid-user') {
        throw new Error('User session expired. Please refresh the page and try again.');
      }
      throw new Error(error.message || 'Failed to send verification code. Please try again.');
    }
  },

  /**
   * Verify SMS code and complete enrollment
   * @param verificationId Verification ID from enrollSms
   * @param code SMS verification code
   */
  async verifySmsCode(verificationId: string, code: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated. Please refresh the page and try again.');
    }

    try {
      // In Firebase v9+, use multiFactor() function
      const mfaUser = multiFactor(user);

      // Get a session for enrollment
      const session = await mfaUser.getSession();

      const credential = PhoneAuthProvider.credential(verificationId, code);

      // Enroll the phone number - session is used internally by the credential
      await mfaUser.enroll(credential, 'Phone Number');

      // Mark as enrolled in backend
      await this.markEnrolled();
    } catch (error: any) {
      console.error('Error verifying SMS code:', error);
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid verification code. Please try again.');
      }
      if (error.code === 'auth/invalid-user' || error.code === 'auth/user-token-expired') {
        throw new Error('User session expired. Please refresh the page and try again.');
      }
      throw new Error(error.message || 'Failed to verify SMS code. Please try again.');
    }
  },

  /**
   * Enroll in Authenticator App MFA
   * @returns Secret and QR code URL for authenticator app
   */
  async enrollAuthenticator(): Promise<{ secret: TotpSecret; qrCodeUrl: string }> {
    // Get current user - ensure we have a fresh reference
    let user = auth.currentUser;

    // If user is null, wait a bit and try again (sometimes there's a timing issue)
    if (!user) {
      await new Promise(resolve => setTimeout(resolve, 200));
      user = auth.currentUser;
    }

    if (!user) {
      throw new Error('Not authenticated. Please refresh the page and log in again.');
    }

    try {
      // In Firebase v9+, use multiFactor() function instead of user.multiFactor property
      const mfaUser = multiFactor(user);

      // Get a session for enrollment
      const session = await mfaUser.getSession();

      // Generate TOTP secret using the session
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

      // Determine environment from API URL
      const apiUrl = import.meta.env.VITE_API_URL || '';
      let envPrefix = '';
      if (apiUrl.includes('staging') || apiUrl.includes('stage')) {
        envPrefix = 'stage:';
      } else if (apiUrl.includes('dev') || apiUrl.includes('localhost')) {
        envPrefix = 'dev:';
      }
      // Production has no prefix (implied)

      // Get username from email (part before @) or use email
      const email = user.email || '';
      const username = email.split('@')[0] || 'User';

      // Generate QR code URL with format:
      // Production: "CAPRI: username"
      // Dev: "CAPRI:dev: username"
      // Staging: "CAPRI:stage: username"
      const issuerName = 'CAPRI';
      const accountName = envPrefix ? `${envPrefix}${username}` : username;
      const qrCodeUrl = totpSecret.generateQrCodeUrl(accountName, issuerName);

      console.log('TOTP secret generated successfully');
      console.log('QR code URL:', qrCodeUrl);

      return {
        secret: totpSecret,
        qrCodeUrl,
      };
    } catch (error: any) {
      console.error('Error generating TOTP secret:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });

      if (error.code === 'auth/requires-recent-login') {
        throw new Error('REQUIRES_RECENT_LOGIN: Please re-enter your password to continue with MFA setup.');
      }
      if (error.code === 'auth/invalid-user' || error.code === 'auth/user-token-expired') {
        throw new Error('User session expired. Please refresh the page and try again.');
      }
      if (error.message?.includes('multiFactor') || error.message?.includes('multi-factor')) {
        throw new Error('Multi-factor authentication is not enabled for your account. Please contact support.');
      }
      throw new Error(error.message || 'Failed to generate authenticator QR code. Please try again.');
    }
  },

  /**
   * Verify Authenticator code and complete enrollment
   * @param secret TOTP secret from enrollAuthenticator (can be TotpSecret object or { secret: string })
   * @param code Code from authenticator app
   */
  async verifyAuthenticatorCode(secret: TotpSecret | { secret: string }, code: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated. Please refresh the page and try again.');
    }

    try {
      // In Firebase v9+, use multiFactor() function
      const mfaUser = multiFactor(user);

      // Get a session for enrollment
      const session = await mfaUser.getSession();

      // Ensure we have a TotpSecret object
      // TotpSecret objects have methods like generateQrCodeUrl, so check for that
      let totpSecret: TotpSecret;
      if (secret && typeof secret === 'object' && 'generateQrCodeUrl' in secret) {
        // It's a TotpSecret object
        totpSecret = secret as TotpSecret;
      } else {
        console.error('Invalid secret format received:', secret);
        throw new Error('Invalid secret format. Please start the enrollment process again.');
      }

      const totpCredential = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        code
      );

      // Enroll - displayName is optional string parameter
      await mfaUser.enroll(totpCredential, 'Authenticator App');

      // Mark as enrolled in backend
      await this.markEnrolled();
    } catch (error: any) {
      console.error('Error verifying authenticator code:', error);
      if (error.code === 'auth/requires-recent-login') {
        throw new Error('REQUIRES_RECENT_LOGIN: Please re-enter your password to complete MFA enrollment.');
      }
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid code. Please check the code from your authenticator app and try again.');
      }
      if (error.code === 'auth/invalid-user' || error.code === 'auth/user-token-expired') {
        throw new Error('User session expired. Please refresh the page and try again.');
      }
      throw new Error(error.message || 'Failed to verify authenticator code. Please try again.');
    }
  },

  /**
   * Handle MFA challenge during sign-in
   * @param error Firebase auth error with multi-factor challenge
   * @param code MFA code from user (TOTP or SMS)
   * @returns Firebase ID token after successful MFA verification
   */
  async handleSignInMfa(error: MultiFactorError, code: string): Promise<string> {
    try {
      // Local development bypass: allow code "111111" to bypass MFA
      const isLocalDev = import.meta.env.DEV && (
        import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' ||
        import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      );

      if (isLocalDev && code === '111111') {
        console.log('🔓 [LOCAL DEV] MFA bypass code "111111" detected - bypassing MFA verification');
        // For local dev bypass, we still need to resolve the MFA challenge
        // The Firebase emulator accepts any 6-digit code for TOTP, so "111111" will work
        const resolver = getMultiFactorResolver(auth, error);
        const hints = resolver.hints;

        if (hints.length === 0) {
          throw new Error('No MFA factors found. Please contact support.');
        }

        const hint = hints[0];

        // Use the bypass code - Firebase emulator accepts any code for TOTP
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
        const userCredential = await resolver.resolveSignIn(assertion);

        if (userCredential && userCredential.user) {
          console.log('✅ [LOCAL DEV] MFA bypass successful - returning token');
          return await userCredential.user.getIdToken();
        }
        throw new Error('Failed to get user from MFA credential');
      }

      const resolver = getMultiFactorResolver(auth, error);
      const hints = resolver.hints;

      if (hints.length === 0) {
        throw new Error('No MFA factors found. Please contact support.');
      }

      // For now, we'll assume TOTP (most common)
      // In the future, we could check hint.factorId to support SMS
      const hint = hints[0];

      if (hint.factorId === 'totp') {
        // TOTP verification
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
        const userCredential = await resolver.resolveSignIn(assertion);

        // Get the user from the credential and return the token
        if (userCredential && userCredential.user) {
          return await userCredential.user.getIdToken();
        }
        throw new Error('Failed to get user from MFA credential');
      } else {
        throw new Error(`Unsupported MFA factor: ${hint.factorId}. Only TOTP is currently supported.`);
      }
    } catch (error: any) {
      console.error('Error handling MFA sign-in:', error);
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid MFA code. Please check and try again.');
      }
      throw new Error(error.message || 'MFA verification failed. Please try again.');
    }
  },
};
